import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { IconField } from '@/components/admin/shared/icon-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ProblemType } from '@/db/schema/enums'
import { PROBLEM_TAG_VOCABULARY } from '@/lib/constants/problem-tags'
import { trpc } from '@/lib/trpc'
import { Badge } from '../shared/badge'
import { Modal } from '../shared/modal'
import { DIFFICULTIES, DIFFICULTY_LABELS } from './constants'

const TYPE_OPTIONS: { value: ProblemType; label: string }[] = [
  { value: 'algorithms', label: 'Algorithms' },
  { value: 'sql', label: 'SQL' },
  { value: 'shell', label: 'Shell' },
]

interface QuestionFormData {
  externalId: string
  name: string
  difficulty: string // Empty string means unassigned
  premiumRequired: boolean
  type: ProblemType
  problemTagSlugs: string[]
  acceptanceRate: string
  freqBar: string
  totalAccepted: string
  totalSubmitted: string
  companies: string[]
  url: string
  editorialUrl: string
  source: string
  sourceIcon: string
}

interface AddQuestionModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: {
    externalId?: string
    name: string
    difficulty?: string | null
    premiumRequired?: boolean
    type?: ProblemType
    problemTagSlugs?: string[]
    acceptanceRate?: number | null
    freqBar?: number | null
    totalAccepted?: number | null
    totalSubmitted?: number | null
    companies?: string[]
    url?: string | null
    editorialUrl?: string | null
    source: string
    sourceIcon?: string | null
  }) => Promise<void>
  sources: string[]
  onAddSource: (source: string) => void
}

export function AddQuestionModal({
  isOpen,
  onClose,
  onSubmit,
  sources,
  onAddSource,
}: AddQuestionModalProps) {
  const [formData, setFormData] = useState<QuestionFormData>({
    externalId: '',
    name: '',
    difficulty: '', // Unassigned by default
    premiumRequired: false,
    type: 'algorithms',
    problemTagSlugs: [],
    acceptanceRate: '',
    freqBar: '',
    totalAccepted: '',
    totalSubmitted: '',
    companies: [],
    url: '',
    editorialUrl: '',
    source: '',
    sourceIcon: '',
  })
  const [problemTagInput, setProblemTagInput] = useState('')
  const [companyInput, setCompanyInput] = useState('')
  const { data: companiesData } = trpc.admin.companies.list.useQuery(
    undefined,
    { enabled: isOpen },
  )
  const companies = companiesData?.data ?? []
  const [showNewSource, setShowNewSource] = useState(false)
  const [showNewCompany, setShowNewCompany] = useState(false)
  const [newSource, setNewSource] = useState('')
  const [newCompany, setNewCompany] = useState({
    name: '',
    slug: '',
    imgUrl: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const createCompanyMutation = trpc.admin.companies.create.useMutation()
  const utils = trpc.useUtils()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target
    const checked =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleAddProblemTag = (slug: string) => {
    if (slug && !formData.problemTagSlugs.includes(slug)) {
      setFormData((prev) => ({
        ...prev,
        problemTagSlugs: [...prev.problemTagSlugs, slug],
      }))
      setProblemTagInput('')
    }
  }

  const handleRemoveProblemTag = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      problemTagSlugs: prev.problemTagSlugs.filter((s) => s !== slug),
    }))
  }

  const handleAddCompany = (companySlug: string) => {
    if (companySlug && !formData.companies.includes(companySlug)) {
      setFormData((prev) => ({
        ...prev,
        companies: [...prev.companies, companySlug],
      }))
      setCompanyInput('')
    }
  }

  const handleRemoveCompany = (companySlug: string) => {
    setFormData((prev) => ({
      ...prev,
      companies: prev.companies.filter((slug) => slug !== companySlug),
    }))
  }

  const handleAddNewSource = () => {
    if (newSource.trim()) {
      onAddSource(newSource.trim())
      setFormData((prev) => ({ ...prev, source: newSource.trim() }))
      setNewSource('')
      setShowNewSource(false)
    }
  }

  const handleAddNewCompany = async () => {
    if (!newCompany.name.trim()) {
      setError('Company name is required')
      return
    }

    try {
      const company = await createCompanyMutation.mutateAsync({
        name: newCompany.name.trim(),
        slug: newCompany.slug.trim() || undefined,
        imgUrl: newCompany.imgUrl.trim() || undefined,
      })

      // Invalidate the companies list so it refetches
      await utils.admin.companies.list.invalidate()
      handleAddCompany(company.slug)
      setNewCompany({ name: '', slug: '', imgUrl: '' })
      setShowNewCompany(false)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create company')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (
      !formData.externalId.trim() ||
      !formData.name.trim() ||
      !formData.source
    ) {
      setError('ID, Name, and Source are required')
      return
    }

    setLoading(true)
    try {
      await onSubmit({
        externalId: formData.externalId,
        name: formData.name,
        difficulty: formData.difficulty || null, // Convert empty string to null
        premiumRequired: formData.premiumRequired,
        type: formData.type,
        problemTagSlugs: formData.problemTagSlugs,
        acceptanceRate: formData.acceptanceRate
          ? Number.parseFloat(formData.acceptanceRate)
          : null,
        freqBar: formData.freqBar ? Number.parseFloat(formData.freqBar) : null,
        totalAccepted: formData.totalAccepted
          ? Number.parseInt(formData.totalAccepted, 10)
          : null,
        totalSubmitted: formData.totalSubmitted
          ? Number.parseInt(formData.totalSubmitted, 10)
          : null,
        companies: formData.companies,
        url: formData.url || null,
        editorialUrl: formData.editorialUrl || null,
        source: formData.source,
        sourceIcon: formData.sourceIcon || null,
      })
      setFormData({
        externalId: '',
        name: '',
        difficulty: '', // Reset to unassigned
        premiumRequired: false,
        type: 'algorithms',
        problemTagSlugs: [],
        acceptanceRate: '',
        freqBar: '',
        totalAccepted: '',
        totalSubmitted: '',
        companies: [],
        url: '',
        editorialUrl: '',
        source: '',
        sourceIcon: '',
      })
      setCompanyInput('')
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add question')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Question">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-destructive/20 border-destructive/30 text-destructive rounded-lg border p-3 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              ID <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              name="externalId"
              value={formData.externalId}
              onChange={handleChange}
              placeholder="e.g., 1 or BCTCI-ch3-1"
              className="font-mono text-sm"
              required
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Difficulty
            </label>
            <select
              name="difficulty"
              value={formData.difficulty}
              onChange={handleChange}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {DIFFICULTY_LABELS[d]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Name <span className="text-destructive">*</span>
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Question name"
            required
          />
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Source <span className="text-destructive">*</span>
          </label>
          {showNewSource ? (
            <div className="flex gap-2">
              <Input
                type="text"
                value={newSource}
                onChange={(e) => setNewSource(e.target.value)}
                placeholder="New source name"
                className="flex-1"
              />
              <Button type="button" onClick={handleAddNewSource} size="sm">
                Add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowNewSource(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                className="border-input bg-background h-9 flex-1 rounded-md border px-3 text-sm"
                required
              >
                <option value="">Select source</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowNewSource(true)}
              >
                <Plus size={14} className="mr-1" />
                New
              </Button>
            </div>
          )}
        </div>

        <div>
          <IconField
            label="Source Icon"
            value={formData.sourceIcon}
            onChange={(sourceIcon) =>
              setFormData((prev) => ({ ...prev, sourceIcon }))
            }
          />
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            URL
          </label>
          <Input
            type="url"
            name="url"
            value={formData.url}
            onChange={handleChange}
            placeholder="https://leetcode.com/problems/..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Acceptance Rate (%)
            </label>
            <Input
              type="number"
              name="acceptanceRate"
              value={formData.acceptanceRate}
              onChange={handleChange}
              placeholder="e.g., 49.1"
              step="0.1"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Frequency Bar (0-100)
            </label>
            <Input
              type="number"
              name="freqBar"
              value={formData.freqBar}
              onChange={handleChange}
              placeholder="e.g., 93.79"
              step="0.1"
              min="0"
              max="100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Total Accepted
            </label>
            <Input
              type="number"
              name="totalAccepted"
              value={formData.totalAccepted}
              onChange={handleChange}
              placeholder="e.g., 1699077"
              min="0"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Total Submitted
            </label>
            <Input
              type="number"
              name="totalSubmitted"
              value={formData.totalSubmitted}
              onChange={handleChange}
              placeholder="e.g., 1901107"
              min="0"
            />
          </div>
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Editorial URL
          </label>
          <Input
            type="url"
            name="editorialUrl"
            value={formData.editorialUrl}
            onChange={handleChange}
            placeholder="Editorial link"
          />
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Type
          </label>
          <div className="flex gap-2">
            {TYPE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={formData.type === option.value ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, type: option.value }))
                }
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Problem Tags
          </label>
          <div className="mb-2 flex gap-2">
            <select
              value={problemTagInput}
              onChange={(e) => setProblemTagInput(e.target.value)}
              className="border-input bg-background h-9 flex-1 rounded-md border px-3 text-sm"
            >
              <option value="">Select tag</option>
              {PROBLEM_TAG_VOCABULARY.filter(
                (t) => !formData.problemTagSlugs.includes(t.slug),
              ).map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (problemTagInput) {
                  handleAddProblemTag(problemTagInput)
                }
              }}
              disabled={!problemTagInput}
            >
              Add
            </Button>
          </div>
          {formData.problemTagSlugs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.problemTagSlugs.map((slug) => {
                const tag = PROBLEM_TAG_VOCABULARY.find((t) => t.slug === slug)
                return (
                  <Badge key={slug} variant="topic" className="gap-1 pr-1">
                    {tag?.name || slug}
                    <button
                      type="button"
                      onClick={() => handleRemoveProblemTag(slug)}
                      className="hover:text-foreground ml-1"
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Companies
          </label>
          {showNewCompany ? (
            <div className="mb-2 space-y-2 rounded-lg border p-3">
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) =>
                    setNewCompany({ ...newCompany, name: e.target.value })
                  }
                  placeholder="Company name"
                  className="flex-1"
                />
                <Input
                  type="text"
                  value={newCompany.slug}
                  onChange={(e) =>
                    setNewCompany({ ...newCompany, slug: e.target.value })
                  }
                  placeholder="Slug (optional)"
                  className="flex-1"
                />
              </div>
              <Input
                type="url"
                value={newCompany.imgUrl}
                onChange={(e) =>
                  setNewCompany({ ...newCompany, imgUrl: e.target.value })
                }
                placeholder="Image URL (optional)"
              />
              <div className="flex gap-2">
                <Button type="button" onClick={handleAddNewCompany} size="sm">
                  Add Company
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowNewCompany(false)
                    setNewCompany({ name: '', slug: '', imgUrl: '' })
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-2 flex gap-2">
              <select
                value={companyInput}
                onChange={(e) => setCompanyInput(e.target.value)}
                className="border-input bg-background h-9 flex-1 rounded-md border px-3 text-sm"
              >
                <option value="">Select company</option>
                {companies
                  .filter((c) => !formData.companies.includes(c.slug))
                  .map((company) => (
                    <option key={company.id} value={company.slug}>
                      {company.name}
                    </option>
                  ))}
              </select>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (companyInput) {
                    handleAddCompany(companyInput)
                  }
                }}
                disabled={!companyInput}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowNewCompany(true)}
              >
                <Plus size={14} className="mr-1" />
                New
              </Button>
            </div>
          )}
          {formData.companies.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.companies.map((companySlug) => {
                const company = companies.find((c) => c.slug === companySlug)
                return (
                  <Badge
                    key={companySlug}
                    variant="topic"
                    className="gap-1 pr-1"
                  >
                    {company?.name || companySlug}
                    <button
                      type="button"
                      onClick={() => handleRemoveCompany(companySlug)}
                      className="hover:text-foreground ml-1"
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              name="premiumRequired"
              checked={formData.premiumRequired}
              onChange={handleChange}
              className="rounded"
            />
            <span className="text-muted-foreground text-sm">
              Premium Required
            </span>
          </label>
        </div>

        <div className="border-border flex justify-end gap-3 border-t pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Adding...' : 'Add Question'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
